import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import {Autocomplete} from "@mui/lab";
import {useState} from "react";
import {fullManualNews} from "../../services/NewsService";
import {useMessageContext} from "../../contexts/MessageContext";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const NewsPlayground = ({setLoading, companies, type}) => {

  const {notificationHandler} = useMessageContext();

  const [data, setData] = useState({
    days: 5,
    maxResults: 5,
    topic: "news",
    query: null,
    company: null,
    advance: true,
    includeAnswer: null,
    includeImages: null,
    includeRawContent: null,
    includeDomains: [],
    excludeDomains: []
  });

  const handleSubmit = () => {
    setLoading(true);

    fullManualNews({
      newsType: type,
      days: data.days,
      maxResults: data.maxResults,
      topic: data.topic,
      query: data.query,
      companyId: data.company?.id,
      advance: data.advance,
      includeAnswer: data.includeAnswer,
      includeImages: data.includeImages,
      includeRawContent: data.includeRawContent,
      includeDomains: data.includeDomains,
      excludeDomains: data.excludeDomains
    })
        .then(() => setData({
          days: 5,
          maxResults: 5,
          topic: "news",
          query: null,
          companyId: null,
          advance: true,
          includeAnswer: null,
          includeImages: null,
          includeRawContent: null,
          includeDomains: [],
          excludeDomains: []
        }))
        .then(() => notificationHandler.success('Manual News triggered'))
        .finally(() => setLoading(false));
  }

  return (
    <Box>
      {/* Basic Settings */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Search Settings
      </Typography>

      <TextField
          fullWidth
          required
          multiline
          rows={2}
          size="small"
          value={data.query || ""}
          margin="dense"
          id="query"
          name="query"
          label="Query"
          placeholder="Enter your search query..."
          onChange={e => setData(prv => ({ ...prv, query: e.target.value }))}
          type="text"
          variant="outlined"
          sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
            required
            size="small"
            value={data.maxResults}
            onChange={e => setData(prv => ({ ...prv, maxResults: e.target.value }))}
            id="max"
            name="max"
            label="Max Results"
            type="number"
            variant="outlined"
            sx={{ flex: 1 }}
        />
        <TextField
            required
            size="small"
            value={data.days}
            onChange={e => setData(prv => ({ ...prv, days: e.target.value }))}
            id="days"
            name="days"
            label="Number of Days"
            type="number"
            variant="outlined"
            sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Company (Optional)</InputLabel>
          <Select
              label="Company (Optional)"
              value={data.company || ""}
              onChange={e => setData(prv => ({ ...prv, company: e.target.value }))}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {companies.map((company, i) => (
              <MenuItem key={i} value={company}>{company.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
            required
            size="small"
            value={data.topic}
            onChange={e => setData(prv => ({ ...prv, topic: e.target.value }))}
            id="topic"
            name="topic"
            label="Topic"
            type="text"
            variant="outlined"
            sx={{ flex: 1 }}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Advanced Settings */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Advanced Options
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Basic</Typography>
          <Switch
            checked={data.advance}
            onChange={() => setData(prv => ({ ...prv, advance: !prv.advance }))}
            size="small"
          />
          <Typography variant="body2">Advanced</Typography>
        </Stack>

        <FormControlLabel
          control={<Switch checked={data.includeAnswer} size="small" />}
          label={<Typography variant="body2">Include Answer</Typography>}
          onChange={() => setData(prv => ({ ...prv, includeAnswer: !prv.includeAnswer }))}
        />

        <FormControlLabel
          control={<Switch checked={data.includeImages} size="small" />}
          label={<Typography variant="body2">Include Images</Typography>}
          onChange={() => setData(prv => ({ ...prv, includeImages: !prv.includeImages }))}
        />

        <FormControlLabel
          control={<Switch checked={data.includeRawContent} size="small" />}
          label={<Typography variant="body2">Include Raw Content</Typography>}
          onChange={() => setData(prv => ({ ...prv, includeRawContent: !prv.includeRawContent }))}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Autocomplete
            multiple
            size="small"
            id="include-domains"
            options={[]}
            freeSolo
            value={data.includeDomains}
            onChange={(event, newValue) => setData(prv => ({ ...prv, includeDomains: newValue }))}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip variant="filled" size="small" label={option} {...getTagProps({ index })} />
                ))
            }
            renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Include Domains" placeholder="Add domain..." />
            )}
            sx={{ flex: 1 }}
        />
        <Autocomplete
            multiple
            size="small"
            id="exclude-domains"
            options={[]}
            freeSolo
            value={data.excludeDomains}
            onChange={(event, newValue) => setData(prv => ({ ...prv, excludeDomains: newValue }))}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip variant="filled" size="small" label={option} {...getTagProps({ index })} />
                ))
            }
            renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Exclude Domains" placeholder="Add domain..." />
            )}
            sx={{ flex: 1 }}
        />
      </Box>

      {/* Submit Button */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            disabled={!data.query || !data.days || !data.maxResults || !data.topic}
            onClick={handleSubmit}
        >
          Generate News
        </Button>
      </Box>
    </Box>
  )
};

export default NewsPlayground;
